import { Fragment, useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { isAdmin } from '../lib/rbac';
import { deleteMeeting, loadMeetings, type Meeting } from '../lib/meetings';
import { fmtDate } from '../components/ui';

// Data/hora curta do comentário na ata (quando houver).
function ataQuando(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return (
    d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) +
    ' · '
  );
}

export function Reunioes() {
  const { currentOrgId, grants } = useAuth();
  const admin = grants ? isAdmin(grants) : false;
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!currentOrgId) return;
    setLoading(true);
    loadMeetings(currentOrgId)
      .then(setMeetings)
      .finally(() => setLoading(false));
  }, [currentOrgId]);

  async function excluir(id: string) {
    if (!confirm('Excluir esta ata? Não afeta os comentários já registrados nos projetos.')) return;
    const { error } = await deleteMeeting(id);
    if (error) return alert(error);
    setMeetings((ms) => ms.filter((m) => m.id !== id));
  }

  const toggle = (id: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <>
      <div className="topbar">
        <h1>Reuniões semanais</h1>
        <div className="stat-strip">
          <div className="stat-chip">
            <span className="n tabular">{meetings.length}</span>
            <span className="l">Atas</span>
          </div>
        </div>
      </div>

      <div className="content">
        {loading ? (
          <div className="center-note">Carregando…</div>
        ) : meetings.length === 0 ? (
          <div className="card card-pad">
            <p className="muted">
              Nenhuma reunião registrada ainda. Ao encerrar uma reunião semanal com registros, a ata
              aparece aqui.
            </p>
          </div>
        ) : (
          meetings.map((m) => {
            const isOpen = open.has(m.id);
            const projetos = m.itens.filter((i) => i.tipo === 'projeto');
            const outros = m.itens.filter((i) => i.tipo === 'outro');
            return (
              <div className="card card-pad ata" key={m.id}>
                <div className="ata-headrow">
                  <button className="ata-head" onClick={() => toggle(m.id)}>
                    <span className="exp-caret">{isOpen ? '▾' : '▸'}</span>
                    <b>{fmtDate(m.heldOn)}</b>
                    <span className="muted">
                      · {m.itens.length} item(ns)
                      {m.createdByName ? ` · ${m.createdByName}` : ''}
                    </span>
                  </button>
                  {admin && (
                    <button className="linklike" onClick={() => excluir(m.id)}>
                      excluir ata
                    </button>
                  )}
                </div>
                {isOpen && (
                  <div className="ata-body">
                    {projetos.length > 0 && (
                      <>
                        <h3 className="enc-h">Projetos tratados</h3>
                        {projetos.map((i, k) =>
                          i.tipo === 'projeto' ? (
                            <div className="ata-item" key={k}>
                              <b>{i.projeto}</b>
                              {i.comentario && <div className="ata-coment">“{i.comentario}”</div>}
                              <div className="muted" style={{ fontSize: 12 }}>
                                {i.autor ? `${i.autor} · ` : ''}
                                {ataQuando(i.quando)}
                                {i.anexo ? `📎 ${i.anexo} · ` : ''}
                                {i.marcados.length ? `Encaminhado a: ${i.marcados.join(', ')}` : ''}
                              </div>
                            </div>
                          ) : null,
                        )}
                      </>
                    )}
                    {outros.length > 0 && (
                      <>
                        <h3 className="enc-h">Outros assuntos</h3>
                        {outros.map((i, k) =>
                          i.tipo === 'outro' ? (
                            <div className="ata-item" key={k}>
                              <b>{i.assunto}</b>
                              {i.comentario && <div className="ata-coment">“{i.comentario}”</div>}
                              <div className="muted" style={{ fontSize: 12 }}>
                                {i.autor ? `${i.autor} · ` : ''}
                                {ataQuando(i.quando)}
                                {i.responsavel ? `Responsável: ${i.responsavel}` : ''}
                              </div>
                            </div>
                          ) : (
                            <Fragment key={k} />
                          ),
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
